import { api } from './api';
import { getPatients } from './patients';
import { getProfessionals } from './professionals';

export const APPOINTMENT_STATUSES = ['SCHEDULED', 'ONGOING', 'COMPLETED', 'CANCELLED'];
const requireAgendaAccess = (actor) => {
    if (!actor?.user?.uuid || actor.role?.name !== 'CLINIC_ADMIN' || actor.role.disabled
        || !actor.role.uuid || !actor.role.account?.uuid) throw new Error('AGENDA_ACCESS_DENIED');
    return { 'CHEART-ROLE': actor.role.uuid };
};
const projectAppointment = (actor, item) => {
    if (!item?.uuid || !item.patient?.uuid || !item.doctor?.uuid
        || !Number.isFinite(Date.parse(item.ts)) || !APPOINTMENT_STATUSES.includes(item.status)) throw new Error('INVALID_APPOINTMENT');
    return {
        uuid: item.uuid, ts: item.ts, status: item.status, slot_duration: item.slot_duration,
        patient: { uuid: item.patient.uuid, name: item.patient.name },
        doctor: { uuid: item.doctor.uuid, name: item.doctor.name },
        accountUuid: actor.role.account.uuid, accessRoleUuid: actor.role.uuid
    };
};
const verifyAppointment = (actor, target) => {
    requireAgendaAccess(actor);
    if (!target?.uuid || target.accountUuid !== actor.role.account.uuid
        || target.accessRoleUuid !== actor.role.uuid) throw new Error('INVALID_APPOINTMENT_CONTEXT');
};
const verifyPerson = (actor, person, role) => {
    if (!person?.role_uuid || person.role !== role || person.disabled
        || person.account?.uuid !== actor.role.account.uuid) throw new Error('INVALID_APPOINTMENT_PARTICIPANT');
};
const validateTime = (ts, duration) => {
    if (!Number.isFinite(Date.parse(ts)) || Date.parse(ts) <= Date.now()
        || !Number.isInteger(duration) || duration < 1) throw new Error('INVALID_APPOINTMENT_TIME');
};

export const fetchAppointments = async (actor, { doctor, statuses = APPOINTMENT_STATUSES, timestamp, endTimestamp, signal }) => {
    const headers = requireAgendaAccess(actor);
    if (!statuses.length || statuses.some((status) => !APPOINTMENT_STATUSES.includes(status))
        || !Number.isFinite(Date.parse(timestamp)) || !Number.isFinite(Date.parse(endTimestamp))
        || Date.parse(timestamp) >= Date.parse(endTimestamp)) throw new Error('INVALID_APPOINTMENT_QUERY');
    const rows = [];
    let token = null;
    let loaded = 0;
    const seen = new Set();
    do {
        const { data, headers: pagination } = await api.get('/cheart/appointments', {
            headers: {
                ...headers, 'CHEART-QUERY-ORDER': 'ASC', 'CHEART-QUERY-TIMESTAMP': timestamp,
                'CHEART-QUERY-APPOINTMENT-STATUS': JSON.stringify(statuses), 'PAGINATION-LIMIT': 50,
                ...(doctor ? { 'CHEART-QUERY-DOCTOR': doctor } : {}),
                ...(token ? { 'PAGINATION-TOKEN': token } : {})
            }, signal
        });
        const count = Number(pagination['pagination-count']);
        if (!Array.isArray(data) || !Number.isInteger(count) || count < 0) throw new Error('INVALID_APPOINTMENT_PAGE');
        const appointments = data.map((item) => projectAppointment(actor, item));
        if (appointments.some((item) => !statuses.includes(item.status) || (doctor && item.doctor.uuid !== doctor))) throw new Error('INVALID_APPOINTMENT_PAGE');
        rows.push(...appointments.filter((item) => Date.parse(item.ts) >= Date.parse(timestamp) && Date.parse(item.ts) < Date.parse(endTimestamp)));
        loaded += data.length;
        if (loaded >= count || appointments.some((item) => Date.parse(item.ts) >= Date.parse(endTimestamp))) break;
        token = pagination['pagination-token'];
        if (!data.length || !token || seen.has(token)) throw new Error('INVALID_APPOINTMENT_PAGE');
        seen.add(token);
    } while (token);
    return rows;
};
export const getAppointmentClinicians = async (actor) => {
    requireAgendaAccess(actor);
    const rows = [];
    let token = null;
    const seen = new Set();
    do {
        const page = await getProfessionals(actor, { roles: ['DOCTOR'], token, pageSize: 50 });
        rows.push(...page.rows);
        if (rows.length >= page.total) break;
        token = page.nextToken;
        if (!page.rows.length || !token || seen.has(token)) throw new Error('INVALID_CLINICIAN_PAGE');
        seen.add(token);
    } while (token);
    return rows;
};
export const getAppointmentPatients = async (actor, options) => {
    requireAgendaAccess(actor);
    const page = await getPatients(actor, options);
    return { users: page.rows, token: page.nextToken, count: page.total };
};
const postAppointment = async (actor, values) => {
    const headers = requireAgendaAccess(actor);
    validateTime(values.ts, values.slot_duration);
    const { data } = await api.post('/cheart/appointments/admin', {
        patient: values.patient, doctor: values.doctor, ts: values.ts,
        slot_duration: values.slot_duration, status: 'SCHEDULED'
    }, { headers });
    const result = projectAppointment(actor, data);
    if (result.patient.uuid !== values.patient || result.doctor.uuid !== values.doctor) throw new Error('INVALID_APPOINTMENT');
    return result;
};
export const scheduleAppointment = async (actor, values) => {
    requireAgendaAccess(actor);
    verifyPerson(actor, values.patient, 'PATIENT');
    verifyPerson(actor, values.doctor, 'DOCTOR');
    return postAppointment(actor, { ...values, patient: values.patient.role_uuid, doctor: values.doctor.role_uuid });
};
const fetchScopedAppointment = async (actor, target) => {
    verifyAppointment(actor, target);
    // This endpoint returns appointment metadata, never fetch the referenced clinical note.
    const { data } = await api.post('/cheart/appointments/fetch', { appointment: target.uuid }, {
        headers: requireAgendaAccess(actor)
    });
    const latest = projectAppointment(actor, data);
    if (latest.uuid !== target.uuid) throw new Error('INVALID_APPOINTMENT');
    return latest;
};
export const cancelAppointment = async (actor, target) => {
    const latest = await fetchScopedAppointment(actor, target);
    if (latest.status === 'CANCELLED') return;
    if (latest.status !== 'SCHEDULED') throw new Error('APPOINTMENT_CHANGED');
    await api.patch('/cheart/appointments/admin', {
        appointment: latest.uuid, status: 'CANCELLED'
    }, { headers: requireAgendaAccess(actor) });
};
export const createRescheduledAppointment = async (actor, target, values) => {
    const latest = await fetchScopedAppointment(actor, target);
    if (latest.status !== 'CANCELLED') throw new Error('ORIGINAL_NOT_CANCELLED');
    return postAppointment(actor, {
        patient: latest.patient.uuid, doctor: latest.doctor.uuid,
        ts: values.ts, slot_duration: values.slot_duration
    });
};
